import { Component } from '@angular/core';
import { EventType, NavigationEnd, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import {
  Observable,
  combineLatest,
  filter,
  from,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { DeliveryLocation } from 'src/app/api/contracts/location';
import { InStoreProduct } from 'src/app/api/contracts/product';
import { rootRoutes } from 'src/app/app-routing.module';
import { ClientService } from 'src/app/services/client.service';
import { InStoreProductsService } from 'src/app/services/in-store-products.service';
import { PermissionService } from 'src/app/services/permission.service';
import { PlatformService } from 'src/app/services/platform.service';
import { AppPermissions } from 'src/app/types/permissions';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  private readonly routesWithTitle: string[] = ['cart', 'order-summary'];
  private readonly routesWithoutDeliveryAddress: string[] = ['order-summary'];
  private readonly routesWithInstoreAddress: string[] = ['scan'];
  private readonly _currentRoute$: Observable<string>;

  public readonly currentRoute$: Observable<string>;
  public readonly shouldDisplayBackButton$: Observable<boolean>;
  public readonly shouldDisplayTitle$: Observable<boolean>;
  public readonly shouldDisplayDeliveryAddress$: Observable<boolean>;
  public readonly shouldDisplayInstorePurchaseAddress$: Observable<boolean>;
  public readonly displayedLocation$: Observable<DeliveryLocation | null>;
  public readonly newProducts$: Observable<InStoreProduct[] | null>;
  public readonly permissions = AppPermissions;

  constructor(
    private readonly router: Router,
    private readonly platformService: PlatformService,
    private readonly navCtrl: NavController,
    private readonly inStoreProductsService: InStoreProductsService,
    public readonly permissionService: PermissionService,
    private readonly clientService: ClientService
  ) {
    this._currentRoute$ = this.router.events.pipe(
      filter((e) => e.type === EventType.NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects?.replace('/', '')),
      shareReplay(1)
    );

    this.currentRoute$ = this._currentRoute$;

    this.shouldDisplayBackButton$ = this._currentRoute$.pipe(
      map((url) => !rootRoutes.includes(url))
    );

    this.shouldDisplayTitle$ = this._currentRoute$.pipe(
      map((route) => this.routesWithTitle.includes(route))
    );

    this.shouldDisplayDeliveryAddress$ = this._currentRoute$.pipe(
      mergeMap((route) =>
        this.permissionService
          .has(this.permissions.ORDER_DELIVERY)
          .pipe(map((canOrderForDelivery) => [route, canOrderForDelivery]))
      ),
      map(
        ([route, canOrderForDelivery]) =>
          !this.routesWithoutDeliveryAddress.includes(route as string) &&
          (canOrderForDelivery as boolean)
      )
    );

    this.shouldDisplayInstorePurchaseAddress$ = combineLatest([
      this._currentRoute$,
      this.inStoreProductsService.currentInstoreSession$,
    ]).pipe(
      map(
        ([route, session]) =>
          this.routesWithInstoreAddress.includes(route) && !!session
      )
    );

    this.newProducts$ = this.inStoreProductsService.newProducts$.asObservable();

    this.displayedLocation$ = this.shouldDisplayInstorePurchaseAddress$.pipe(
      mergeMap((shouldDisplayInstorePurchaseAddress) => {
        if (shouldDisplayInstorePurchaseAddress) {
          return this.inStoreProductsService.currentInstoreSession$.pipe(
            map((session) => session?.location || null)
          );
        }
        return this.clientService.clientLocation$;
      })
    );
  }

  onLogoClick(): void {
    this.newProducts$.pipe(take(1)).subscribe((products) => {
      if (products === null || products.length === 0) {
        return;
      }
      this.navCtrl.navigateRoot('in-store-products');
    });
  }

  onBackButtonClick(): void {
    from(this.navCtrl.pop()) // pop() already makes back navigation
      .pipe(take(1))
      .subscribe((hasHistory) => {
        if (!hasHistory) {
          this.navCtrl.navigateRoot('store');
        }
      });
  }
}
